/**
 * Skaper – abonnementsplaner (per plattform + eget brukernavn).
 *
 * URL: /skaper/sosial-abonnenter/planer
 */
import React, { useEffect, useMemo, useState } from "react";
import { Link, Navigate } from "react-router-dom";
import { ArrowLeft, Plus, X, Trash2, Pencil, Save } from "lucide-react";
import { toast } from "sonner";
import Layout from "../components/Layout";
import { useAuth } from "../contexts/AuthContext";
import api, { formatApiError } from "../lib/api";

const CURRENCIES = ["NOK", "EUR", "USD", "SEK", "DKK", "GBP"];
const emptyPlan = () => ({
  platform: "",
  platform_account: "",
  name: "",
  description: "",
  periods: [{ label: "1 måned", days: 30, price: "" }],
  currency: "NOK",
  active: true,
});

function PlanForm({ initial, platforms, onSave, onCancel }) {
  const [f, setF] = useState(() => {
    if (!initial) {
      const p = emptyPlan();
      p.platform = platforms[0] || "Snapchat";
      return p;
    }
    return {
      ...initial,
      platform_account: initial.platform_account || "",
      description: initial.description || "",
      periods: (initial.periods || []).map((p) => ({ ...p, price: String(p.price ?? "") })),
    };
  });
  const [saving, setSaving] = useState(false);

  const set = (k, v) => setF((s) => ({ ...s, [k]: v }));

  const setPeriod = (i, k, v) => {
    setF((s) => ({
      ...s,
      periods: s.periods.map((p, j) => (j === i ? { ...p, [k]: v } : p)),
    }));
  };

  const addPeriod = () => setF((s) => ({ ...s, periods: [...s.periods, { label: "", days: 30, price: "" }] }));
  const rmPeriod = (i) => setF((s) => ({ ...s, periods: s.periods.filter((_, j) => j !== i) }));

  const submit = async () => {
    if (!f.name.trim()) return toast.error("Navn er påkrevd");
    if (!f.platform) return toast.error("Plattform er påkrevd");
    if (f.periods.length === 0) return toast.error("Legg til minst én periode");
    for (const p of f.periods) {
      if (!p.label?.trim()) return toast.error("Alle perioder må ha en tittel");
      if (!p.days || Number(p.days) <= 0) return toast.error("Perioder må ha dager > 0");
      if (p.price === "" || Number(p.price) < 0) return toast.error("Perioder må ha pris ≥ 0");
    }
    setSaving(true);
    const payload = {
      platform: f.platform,
      platform_account: f.platform_account.trim() || null,
      name: f.name.trim(),
      description: f.description.trim() || null,
      currency: f.currency,
      active: f.active,
      periods: f.periods.map((p) => ({
        id: p.id,
        label: p.label.trim(),
        days: Number(p.days),
        price: Number(p.price),
      })),
    };
    try {
      await onSave(payload);
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 bg-black/70 flex items-end sm:items-center justify-center p-0 sm:p-4" data-testid="plan-form-overlay">
      <div className="bg-[var(--noir-elev)] border border-white/10 w-full sm:max-w-2xl sm:rounded-lg overflow-y-auto max-h-[95vh]">
        <div className="p-4 border-b border-white/10 flex items-center justify-between sticky top-0 bg-[var(--noir-elev)] z-10">
          <h3 className="font-fraunces italic text-xl text-[var(--cream)]">
            {initial ? "Rediger plan" : "Ny abonnementsplan"}
          </h3>
          <button onClick={onCancel} className="btn-ghost p-1.5" data-testid="plan-form-close"><X className="h-4 w-4"/></button>
        </div>

        <div className="p-4 space-y-3">
          <div className="grid grid-cols-2 gap-3">
            <label className="block">
              <span className="text-[10px] uppercase tracking-[0.15em] text-[var(--cream-soft)]">Plattform*</span>
              <select value={f.platform} onChange={(e)=>set("platform", e.target.value)}
                className="input-editorial w-full mt-1" data-testid="plan-platform">
                {platforms.map(p => <option key={p} value={p}>{p}</option>)}
              </select>
            </label>
            <label className="block">
              <span className="text-[10px] uppercase tracking-[0.15em] text-[var(--cream-soft)]">Ditt brukernavn</span>
              <input value={f.platform_account} onChange={(e)=>set("platform_account", e.target.value)}
                placeholder="@dittSnap" className="input-editorial w-full mt-1" data-testid="plan-account" />
            </label>
          </div>

          <label className="block">
            <span className="text-[10px] uppercase tracking-[0.15em] text-[var(--cream-soft)]">Navn*</span>
            <input value={f.name} onChange={(e)=>set("name", e.target.value)}
              placeholder="GFE, Filmer, Bilder …" className="input-editorial w-full mt-1" data-testid="plan-name" />
          </label>

          <label className="block">
            <span className="text-[10px] uppercase tracking-[0.15em] text-[var(--cream-soft)]">Beskrivelse (valgfri)</span>
            <textarea value={f.description} onChange={(e)=>set("description", e.target.value)}
              rows={2} className="input-editorial w-full mt-1" data-testid="plan-desc" />
          </label>

          <div className="border-t border-white/10 pt-3">
            <div className="flex items-center justify-between mb-2">
              <div className="font-fraunces italic text-[var(--cream)]">Perioder</div>
              <button onClick={addPeriod} className="btn-ghost text-xs inline-flex items-center gap-1" data-testid="plan-add-period">
                <Plus className="h-3 w-3" /> Ny periode
              </button>
            </div>
            <div className="space-y-2">
              {f.periods.map((p, i) => (
                <div key={i} className="grid grid-cols-[1fr_80px_100px_auto] gap-2 items-end">
                  <label className="block">
                    <span className="text-[9px] uppercase tracking-[0.15em] text-[var(--cream-soft)]">Tittel</span>
                    <input value={p.label} onChange={(e)=>setPeriod(i, "label", e.target.value)}
                      placeholder="1 uke, 1 måned…" className="input-editorial w-full mt-0.5" data-testid={`period-label-${i}`} />
                  </label>
                  <label className="block">
                    <span className="text-[9px] uppercase tracking-[0.15em] text-[var(--cream-soft)]">Dager</span>
                    <input type="number" min="1" value={p.days} onChange={(e)=>setPeriod(i, "days", e.target.value)}
                      className="input-editorial w-full mt-0.5" data-testid={`period-days-${i}`} />
                  </label>
                  <label className="block">
                    <span className="text-[9px] uppercase tracking-[0.15em] text-[var(--cream-soft)]">Pris</span>
                    <input type="number" min="0" step="1" value={p.price} onChange={(e)=>setPeriod(i, "price", e.target.value)}
                      className="input-editorial w-full mt-0.5" data-testid={`period-price-${i}`} />
                  </label>
                  <button onClick={() => rmPeriod(i)}
                    className="btn-ghost p-1.5 text-rose-400 hover:text-rose-300 self-end mb-0.5"
                    data-testid={`period-remove-${i}`} title="Fjern"><Trash2 className="h-3.5 w-3.5" /></button>
                </div>
              ))}
            </div>
          </div>

          <div className="grid grid-cols-2 gap-3 border-t border-white/10 pt-3">
            <label className="block">
              <span className="text-[10px] uppercase tracking-[0.15em] text-[var(--cream-soft)]">Valuta</span>
              <select value={f.currency} onChange={(e)=>set("currency", e.target.value)}
                className="input-editorial w-full mt-1" data-testid="plan-currency">
                {CURRENCIES.map(c => <option key={c} value={c}>{c}</option>)}
              </select>
            </label>
            <label className="block">
              <span className="text-[10px] uppercase tracking-[0.15em] text-[var(--cream-soft)]">Aktiv</span>
              <select value={f.active ? "1" : "0"} onChange={(e)=>set("active", e.target.value === "1")}
                className="input-editorial w-full mt-1" data-testid="plan-active">
                <option value="1">Ja</option>
                <option value="0">Arkivert</option>
              </select>
            </label>
          </div>
        </div>

        <div className="p-4 border-t border-white/10 flex gap-2 sticky bottom-0 bg-[var(--noir-elev)]">
          <button onClick={onCancel} className="btn-ghost flex-1">Avbryt</button>
          <button onClick={submit} disabled={saving} className="btn-magenta flex-1 inline-flex items-center justify-center gap-1.5" data-testid="plan-form-save">
            <Save className="h-3.5 w-3.5" /> {saving ? "Lagrer…" : "Lagre"}
          </button>
        </div>
      </div>
    </div>
  );
}

export default function SkaperAbonnementsplaner() {
  const { user, loading } = useAuth();
  const [platforms, setPlatforms] = useState([]);
  const [plans, setPlans] = useState([]);
  const [busy, setBusy] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [editing, setEditing] = useState(null);

  const load = async () => {
    setBusy(true);
    try {
      const [r1, r2] = await Promise.all([
        api.get("/me/social-subs/meta/platforms"),
        api.get("/me/social-subs/plans"),
      ]);
      setPlatforms(r1.data.platforms || []);
      setPlans(r2.data.plans || []);
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setBusy(false);
    }
  };

  useEffect(() => {
    if (user?.is_creator || user?.role === "owner" || user?.role === "admin") load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user?.id]);

  const grouped = useMemo(() => {
    const g = {};
    for (const p of plans) {
      const key = `${p.platform}::${p.platform_account || "(uten brukernavn)"}`;
      if (!g[key]) g[key] = { platform: p.platform, platform_account: p.platform_account, items: [] };
      g[key].items.push(p);
    }
    return Object.values(g);
  }, [plans]);

  const doSave = async (payload) => {
    try {
      if (editing) {
        await api.patch(`/me/social-subs/plans/${editing.id}`, payload);
        toast.success("Oppdatert");
      } else {
        await api.post(`/me/social-subs/plans`, payload);
        toast.success("Plan opprettet");
      }
      setShowForm(false);
      setEditing(null);
      load();
    } catch (e) {
      toast.error(formatApiError(e));
    }
  };

  const doDelete = async (p) => {
    if (!window.confirm(`Slette planen «${p.name}»? Eksisterende abonnementer beholdes med navne-snapshot.`)) return;
    try {
      await api.delete(`/me/social-subs/plans/${p.id}`);
      toast.success("Slettet");
      load();
    } catch (e) {
      toast.error(formatApiError(e));
    }
  };

  if (loading) return null;
  if (!user) return <Navigate to="/login" replace />;
  const isCreator = user.is_creator || user.role === "owner" || user.role === "admin";
  if (!isCreator) return <Navigate to="/" replace />;

  return (
    <Layout>
      <div className="max-w-5xl mx-auto px-4 py-6" data-testid="plans-page">
        <div className="mb-4">
          <Link to="/skaper/sosial-abonnenter" className="text-[var(--cream-faint)] hover:text-[var(--magenta)] inline-flex items-center gap-1.5" data-testid="plans-back">
            <ArrowLeft className="h-4 w-4" /> Abonnenter
          </Link>
        </div>

        <div className="flex flex-wrap items-end justify-between gap-4 mb-2">
          <h1 className="font-fraunces italic text-3xl sm:text-4xl text-[var(--cream)]">Abonnementsplaner</h1>
          <button onClick={() => { setEditing(null); setShowForm(true); }}
            className="btn-magenta inline-flex items-center gap-2" data-testid="plans-new-btn">
            <Plus className="h-4 w-4" /> Ny plan
          </button>
        </div>
        <p className="text-sm text-[var(--cream-soft)] mb-6">
          Ferdigdefinerte planer med flere periode-alternativer. Grupper etter plattform og ditt eget brukernavn.
        </p>

        {busy && <div className="text-center py-8 text-[var(--cream-faint)]">Henter…</div>}

        {!busy && plans.length === 0 && (
          <div className="border border-white/10 p-8 text-center font-fraunces italic text-[var(--cream-soft)]">
            Ingen planer ennå. Klikk «Ny plan» for å komme i gang.
          </div>
        )}

        {!busy && grouped.map((g) => (
          <div key={`${g.platform}::${g.platform_account}`} className="mb-6" data-testid={`plans-group-${g.platform}`}>
            <div className="flex items-baseline gap-3 mb-2">
              <div className="font-fraunces italic text-xl text-[var(--cream)]">{g.platform}</div>
              {g.platform_account && <div className="text-[var(--cream-soft)] text-sm">{g.platform_account}</div>}
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
              {g.items.map((p) => (
                <div key={p.id} className={`p-4 border ${p.active ? "border-white/10 bg-[var(--noir-elev)]" : "border-white/5 bg-white/[0.01] opacity-70"}`} data-testid={`plan-card-${p.id}`}>
                  <div className="flex items-start justify-between gap-2 mb-2">
                    <div className="font-fraunces italic text-lg text-[var(--cream)] truncate">{p.name}</div>
                    <div className="flex gap-1 shrink-0">
                      <button onClick={() => { setEditing(p); setShowForm(true); }} className="btn-ghost p-1.5" title="Rediger" data-testid={`plan-edit-${p.id}`}>
                        <Pencil className="h-3.5 w-3.5" />
                      </button>
                      <button onClick={() => doDelete(p)} className="btn-ghost p-1.5 text-rose-400 hover:text-rose-300" title="Slett" data-testid={`plan-delete-${p.id}`}>
                        <Trash2 className="h-3.5 w-3.5" />
                      </button>
                    </div>
                  </div>
                  {p.description && <div className="text-[var(--cream-soft)] text-xs mb-2">{p.description}</div>}
                  <div className="space-y-1">
                    {p.periods.map((per) => (
                      <div key={per.id} className="flex justify-between text-xs font-manrope">
                        <span className="text-[var(--cream)]">{per.label} <span className="text-[var(--cream-faint)]">({per.days} d)</span></span>
                        <span className="text-[var(--cream)]">{per.price} {p.currency}</span>
                      </div>
                    ))}
                  </div>
                </div>
              ))}
            </div>
          </div>
        ))}

        {showForm && (
          <PlanForm
            initial={editing}
            platforms={platforms}
            onSave={doSave}
            onCancel={() => { setShowForm(false); setEditing(null); }}
          />
        )}
      </div>
    </Layout>
  );
}
